import { Box, CircularProgress, Typography } from "@mui/material";

export interface StreamProgress {
	loaded: number;
	/** Estimated total (the previous completed load for the same scope), or null on a first load. */
	total: number | null;
}

/** "Loaded 3,200 of ~7,000 jobs" while streaming with an estimate, count-only on a first load. */
export function streamProgressLabel(progress: StreamProgress): string {
	if (progress.total) {
		return `Loaded ${progress.loaded.toLocaleString()} of ~${progress.total.toLocaleString()} jobs`;
	}
	return `Loading jobs... (${progress.loaded.toLocaleString()} so far)`;
}

/** Progress percentage against the estimated total, capped below 100 since the estimate may run short. */
export function streamProgressValue(progress: StreamProgress): number | null {
	if (!progress.total) return null;
	return Math.min(99, (progress.loaded / progress.total) * 100);
}

export interface StreamProgressIndicatorProps {
	/** Percent complete (0-100), or null when the total is unknown (first load of a scope). */
	value: number | null;
	size?: number;
}

/**
 * Progress indicator for the streaming jobs load: a determinate ring over a
 * faint full-circle track with the percentage in the center, falling back to
 * an indeterminate spinner when there's no estimated total yet.
 */
export function StreamProgressIndicator({ value, size = 28 }: StreamProgressIndicatorProps) {
	if (value === null) {
		return <CircularProgress size={size} thickness={5} />;
	}
	return (
		<Box sx={{ position: "relative", display: "inline-flex" }}>
			<CircularProgress
				variant="determinate"
				value={100}
				size={size}
				thickness={5}
				sx={{ color: (theme) => theme.palette.grey[300] }}
			/>
			<CircularProgress
				variant="determinate"
				value={value}
				size={size}
				thickness={5}
				sx={{ position: "absolute", left: 0, top: 0 }}
			/>
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography
					variant="caption"
					component="div"
					sx={{ typography: "micro", fontWeight: 700, lineHeight: 1, color: "primary.main" }}
				>
					{`${Math.round(value)}%`}
				</Typography>
			</Box>
		</Box>
	);
}
